import * as THREE from "three";
import { createSuntraceTrack, wrapProgress, type TrackFrame } from "./track";
import type { RacePresentationSnapshot, SuntraceQuality } from "./types";

const createRandom = (seed: number): (() => number) => {
  let state = seed >>> 0;
  return () => {
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    return (state >>> 0) / 4_294_967_296;
  };
};

const createParticleTexture = (horizontalCompression = 1): THREE.DataTexture => {
  const size = 32;
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const normalizedX = (x + 0.5) / size * 2 - 1;
      const normalizedY = (y + 0.5) / size * 2 - 1;
      const compressedX = normalizedX * horizontalCompression;
      const distance = Math.sqrt(compressedX * compressedX + normalizedY * normalizedY);
      const alpha = Math.round(Math.pow(Math.max(0, 1 - distance), 2) * 255);
      const offset = (y * size + x) * 4;
      data[offset] = 255;
      data[offset + 1] = 255;
      data[offset + 2] = 255;
      data[offset + 3] = alpha;
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.needsUpdate = true;
  return texture;
};

class ParticlePool {
  readonly points: THREE.Points;
  readonly geometry: THREE.BufferGeometry;
  readonly material: THREE.PointsMaterial;
  readonly texture: THREE.DataTexture;
  readonly positions: Float32Array;
  readonly velocities: Float32Array;
  readonly colors: Float32Array;
  readonly baseColors: Float32Array;
  readonly ages: Float32Array;
  readonly lifetimes: Float32Array;
  readonly count: number;
  #cursor = 0;

  constructor(
    count: number,
    color: number,
    size: number,
    additive: boolean,
    horizontalCompression = 1,
  ) {
    this.count = count;
    this.positions = new Float32Array(count * 3);
    this.velocities = new Float32Array(count * 3);
    this.colors = new Float32Array(count * 3);
    this.baseColors = new Float32Array(count * 3);
    this.ages = new Float32Array(count).fill(1);
    this.lifetimes = new Float32Array(count).fill(0.01);
    this.geometry = new THREE.BufferGeometry();
    this.geometry.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geometry.setAttribute("color", new THREE.BufferAttribute(this.colors, 3));
    this.texture = createParticleTexture(horizontalCompression);
    this.material = new THREE.PointsMaterial({
      color,
      size,
      map: this.texture,
      transparent: true,
      opacity: 0.92,
      alphaTest: 0.02,
      depthWrite: false,
      blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending,
      sizeAttenuation: true,
      vertexColors: true,
    });
    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.points.renderOrder = 3;
  }

  emit(
    position: THREE.Vector3,
    velocity: THREE.Vector3,
    lifetime: number,
    color: THREE.Color,
  ): void {
    const index = this.#cursor;
    const offset = index * 3;
    this.positions[offset] = position.x;
    this.positions[offset + 1] = position.y;
    this.positions[offset + 2] = position.z;
    this.velocities[offset] = velocity.x;
    this.velocities[offset + 1] = velocity.y;
    this.velocities[offset + 2] = velocity.z;
    this.colors[offset] = color.r;
    this.colors[offset + 1] = color.g;
    this.colors[offset + 2] = color.b;
    this.baseColors[offset] = color.r;
    this.baseColors[offset + 1] = color.g;
    this.baseColors[offset + 2] = color.b;
    this.ages[index] = 0;
    this.lifetimes[index] = Math.max(0.01, lifetime);
    this.#cursor = (this.#cursor + 1) % this.count;
  }

  update(deltaSeconds: number, drag: number, gravity: number): void {
    if (deltaSeconds <= 0) {
      return;
    }
    for (let index = 0; index < this.count; index += 1) {
      const age = this.ages[index];
      if (age === undefined || age >= 1) {
        continue;
      }
      const lifetime = this.lifetimes[index] ?? 0.01;
      const nextAge = Math.min(1, age + deltaSeconds / lifetime);
      this.ages[index] = nextAge;
      const offset = index * 3;
      const dragFactor = Math.exp(-drag * deltaSeconds);
      this.velocities[offset] = (this.velocities[offset] ?? 0) * dragFactor;
      this.velocities[offset + 1] =
        (this.velocities[offset + 1] ?? 0) * dragFactor - gravity * deltaSeconds;
      this.velocities[offset + 2] = (this.velocities[offset + 2] ?? 0) * dragFactor;
      this.positions[offset] =
        (this.positions[offset] ?? 0) + (this.velocities[offset] ?? 0) * deltaSeconds;
      this.positions[offset + 1] =
        (this.positions[offset + 1] ?? 0) +
        (this.velocities[offset + 1] ?? 0) * deltaSeconds;
      this.positions[offset + 2] =
        (this.positions[offset + 2] ?? 0) +
        (this.velocities[offset + 2] ?? 0) * deltaSeconds;
      const fade = Math.pow(1 - nextAge, 1.5);
      this.colors[offset] = (this.baseColors[offset] ?? 0) * fade;
      this.colors[offset + 1] = (this.baseColors[offset + 1] ?? 0) * fade;
      this.colors[offset + 2] = (this.baseColors[offset + 2] ?? 0) * fade;
      if (nextAge >= 1) {
        this.positions[offset + 1] = -10_000;
      }
    }
    const positionAttribute = this.geometry.getAttribute("position");
    const colorAttribute = this.geometry.getAttribute("color");
    positionAttribute.needsUpdate = true;
    colorAttribute.needsUpdate = true;
  }

  dispose(): void {
    this.points.removeFromParent();
    this.geometry.dispose();
    this.material.dispose();
    this.texture.dispose();
  }
}

const wetWakeNoise = (x: number, y: number): number => {
  let value = Math.imul(x + 0x68bc21eb, 0x27d4eb2d);
  value ^= Math.imul(y + 0x165667b1, 0x85ebca6b);
  value ^= value >>> 15;
  value = Math.imul(value, 0x2c1b3c6d);
  value ^= value >>> 12;
  return (value >>> 0) / 4_294_967_296;
};

export const createWetWakeAlphaData = (
  width = 64,
  height = 128,
  phase = 0,
): Uint8Array => {
  const safeWidth = Math.max(8, Math.floor(width));
  const safeHeight = Math.max(16, Math.floor(height));
  const safePhase = Number.isFinite(phase) ? phase : 0;
  const phaseX = Math.floor(safePhase * 89);
  const phaseY = Math.floor(safePhase * 131);
  const alphaData = new Uint8Array(safeWidth * safeHeight);
  for (let y = 0; y < safeHeight; y += 1) {
    const longitudinal = (y + 0.5) / safeHeight;
    const widening = 0.18 + Math.pow(longitudinal, 0.7) * 0.82;
    const coarseFlow = wetWakeNoise(phaseX + 3, phaseY + Math.floor(y / 7));
    const edgeWander =
      Math.sin(longitudinal * 27.6 + safePhase * 4.7 + coarseFlow * 5.2) * 0.05 +
      (coarseFlow - 0.5) * 0.06;
    const endFade = Math.min(1, longitudinal * 10, (1 - longitudinal) * 7);
    for (let x = 0; x < safeWidth; x += 1) {
      const lateral = (x + 0.5) / safeWidth * 2 - 1 - edgeWander;
      const normalizedLateral = Math.abs(lateral) / widening;
      const edgeAlpha = Math.pow(Math.max(0, 1 - normalizedLateral), 0.62);
      const patchNoise =
        wetWakeNoise(
          phaseX + Math.floor(x / 4),
          phaseY + Math.floor(y / 4),
        ) *
          0.68 +
        wetWakeNoise(phaseX + x, phaseY + y) * 0.32;
      const diagonalFlow =
        Math.sin(x * 0.31 + y * 0.17 + safePhase * 6.1) * 0.16;
      const breakup = THREE.MathUtils.smoothstep(
        patchNoise + diagonalFlow,
        0.24,
        0.78,
      );
      const filament =
        0.62 +
        wetWakeNoise(phaseX + Math.floor(x / 2), phaseY + Math.floor(y / 2)) *
          0.25 +
        wetWakeNoise(phaseX + x * 2, phaseY + y * 2) * 0.13;
      alphaData[y * safeWidth + x] = Math.round(
        edgeAlpha * (0.28 + breakup * 0.72) * filament * endFade * 228,
      );
    }
  }
  return alphaData;
};

const createWetWakeTexture = (phase: number): THREE.DataTexture => {
  const width = 64;
  const height = 128;
  const alphaData = createWetWakeAlphaData(width, height, phase);
  const data = new Uint8Array(width * height * 4);
  for (let index = 0; index < alphaData.length; index += 1) {
    const offset = index * 4;
    data[offset] = 226;
    data[offset + 1] = 239;
    data[offset + 2] = 242;
    data[offset + 3] = alphaData[index] ?? 0;
  }
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.colorSpace = THREE.NoColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
};

const createWetWake = (count: number, phase: number) => {
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.rotateX(-Math.PI / 2);
  const texture = createWetWakeTexture(phase);
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    map: texture,
    transparent: true,
    opacity: 0,
    alphaTest: 0.008,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -1,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  const ages = new Float32Array(count).fill(1);
  const lifetimes = new Float32Array(count).fill(0.01);
  const colors = Array.from({ length: count }, () => new THREE.Color());
  const dummy = new THREE.Object3D();
  const fadedColor = new THREE.Color();
  const localYaw = new THREE.Quaternion();
  const localUp = new THREE.Vector3(0, 1, 0);
  let cursor = 0;
  let capacity = count;
  for (let index = 0; index < count; index += 1) {
    dummy.position.set(0, -10_000, 0);
    dummy.scale.setScalar(0.001);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
    mesh.setColorAt(index, fadedColor.setRGB(0, 0, 0));
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor !== null) {
    mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    mesh.instanceColor.needsUpdate = true;
  }

  return {
    mesh,
    material,
    emit(
      position: THREE.Vector3,
      quaternion: THREE.Quaternion,
      lifetime: number,
      length: number,
      width: number,
      color: THREE.Color,
      yaw: number,
    ) {
      ages[cursor] = 0;
      lifetimes[cursor] = Math.max(0.01, lifetime);
      colors[cursor]?.copy(color);
      dummy.position.copy(position);
      localYaw.setFromAxisAngle(localUp, yaw);
      dummy.quaternion.copy(quaternion).multiply(localYaw);
      dummy.scale.set(width, 1, length);
      dummy.updateMatrix();
      mesh.setMatrixAt(cursor, dummy.matrix);
      mesh.setColorAt(cursor, color);
      cursor = (cursor + 1) % capacity;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) {
        mesh.instanceColor.needsUpdate = true;
      }
    },
    update(deltaSeconds: number) {
      if (deltaSeconds <= 0) {
        return;
      }
      let matrixChanged = false;
      for (let index = 0; index < capacity; index += 1) {
        const age = ages[index];
        if (age === undefined || age >= 1) {
          continue;
        }
        const nextAge = Math.min(1, age + deltaSeconds / (lifetimes[index] ?? 0.01));
        ages[index] = nextAge;
        const color = colors[index];
        if (color !== undefined) {
          mesh.setColorAt(index, fadedColor.copy(color).multiplyScalar(Math.pow(1 - nextAge, 1.7)));
        }
        if (nextAge >= 1) {
          dummy.position.set(0, -10_000, 0);
          dummy.scale.setScalar(0.001);
          dummy.updateMatrix();
          mesh.setMatrixAt(index, dummy.matrix);
          matrixChanged = true;
        }
      }
      if (matrixChanged) {
        mesh.instanceMatrix.needsUpdate = true;
      }
      if (mesh.instanceColor !== null) {
        mesh.instanceColor.needsUpdate = true;
      }
    },
    setCapacity(nextCapacity: number) {
      const normalizedCapacity = THREE.MathUtils.clamp(
        Math.floor(nextCapacity),
        8,
        count,
      );
      for (let index = normalizedCapacity; index < capacity; index += 1) {
        ages[index] = 1;
        dummy.position.set(0, -10_000, 0);
        dummy.scale.setScalar(0.001);
        dummy.updateMatrix();
        mesh.setMatrixAt(index, dummy.matrix);
        mesh.setColorAt(index, fadedColor.setRGB(0, 0, 0));
      }
      capacity = normalizedCapacity;
      mesh.count = capacity;
      cursor %= capacity;
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) {
        mesh.instanceColor.needsUpdate = true;
      }
    },
    dispose() {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
  };
};

const createSpeedStreaks = (count: number): {
  mesh: THREE.Mesh;
  geometry: THREE.BufferGeometry;
  material: THREE.MeshBasicMaterial;
  positions: Float32Array;
} => {
  const positions = new Float32Array(count * 18);
  const colors = new Float32Array(count * 18);
  for (let index = 0; index < count; index += 1) {
    const variation = ((index * 37) % 11) / 10;
    const headLuminance = 0.66 + variation * 0.48;
    const tailLuminance = headLuminance * (0.16 + variation * 0.1);
    const offset = index * 18;
    const luminances = [
      headLuminance,
      tailLuminance,
      headLuminance,
      headLuminance,
      tailLuminance,
      tailLuminance,
    ];
    for (let vertex = 0; vertex < luminances.length; vertex += 1) {
      const luminance = luminances[vertex] ?? 0;
      const vertexOffset = offset + vertex * 3;
      colors[vertexOffset] = luminance * 0.78;
      colors[vertexOffset + 1] = luminance * 0.9;
      colors[vertexOffset + 2] = luminance * 0.94;
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const material = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    vertexColors: true,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.Mesh(geometry, material);
  mesh.frustumCulled = false;
  mesh.renderOrder = 4;
  return { mesh, geometry, material, positions };
};

export const calculateSpeedStreakProfile = (speed: number, boost = 0) => {
  const safeSpeed = Number.isFinite(speed) ? Math.max(0, speed) : 0;
  const safeBoost = Number.isFinite(boost)
    ? THREE.MathUtils.clamp(boost, 0, 1)
    : 0;
  const speedIntensity = THREE.MathUtils.smoothstep(safeSpeed, 40, 72);
  const velocitySeparation = THREE.MathUtils.smoothstep(safeSpeed, 58, 84);
  const visibility = THREE.MathUtils.smoothstep(safeSpeed, 35, 48);
  const intensity = THREE.MathUtils.clamp(
    speedIntensity * 0.64 + safeBoost * 0.76,
    0,
    1,
  );
  const nominalWidth =
    0.038 + speedIntensity * 0.021 + velocitySeparation * 0.015;
  const opacity = safeSpeed <= 35
    ? 0
    : THREE.MathUtils.clamp(
        (THREE.MathUtils.lerp(0.05, 0.115, velocitySeparation) +
          safeBoost * 0.045) *
          visibility,
        0,
        0.115,
      );

  return {
    intensity,
    speedIntensity,
    velocitySeparation,
    opacity,
    minimumWidth: nominalWidth * 0.95,
    maximumWidth: nominalWidth * 1.05,
    visible: safeSpeed > 35 && intensity > 0.025 && opacity > 0.001,
  };
};

export type DriftSprayProfile = {
  emissionsPerSecond: number;
  contactSpread: number;
  velocityInheritance: number;
  wakeVelocity: number;
  liftVelocity: number;
  lifetime: number;
  speedFactor: number;
  driftFactor: number;
};

const createEmptyDriftSprayProfile = (): DriftSprayProfile => ({
  emissionsPerSecond: 0,
  contactSpread: 0,
  velocityInheritance: 0,
  wakeVelocity: 0,
  liftVelocity: 0,
  lifetime: 0,
  speedFactor: 0,
  driftFactor: 0,
});

export const calculateDriftSprayProfile = (
  speed: number,
  driftIntensity: number,
  quality: SuntraceQuality,
  reducedMotion: boolean,
  target: DriftSprayProfile = createEmptyDriftSprayProfile(),
): DriftSprayProfile => {
  const safeSpeed = Number.isFinite(speed) ? Math.max(0, speed) : 0;
  const safeDrift = Number.isFinite(driftIntensity)
    ? THREE.MathUtils.clamp(Math.abs(driftIntensity), 0, 1)
    : 0;
  const speedFactor = THREE.MathUtils.smoothstep(safeSpeed, 16, 72);
  const driftFactor = THREE.MathUtils.smoothstep(safeDrift, 0.12, 0.8);
  const qualityFactor = quality === "high" ? 1.18 : quality === "medium" ? 0.76 : 0.26;
  const emissionsPerSecond = reducedMotion
    ? 0
    : speedFactor * (22 + driftFactor * 210) * qualityFactor;

  target.emissionsPerSecond = emissionsPerSecond;
  target.contactSpread = THREE.MathUtils.lerp(0.12, 0.32, driftFactor);
  target.velocityInheritance = THREE.MathUtils.lerp(0.2, 0.38, speedFactor);
  target.wakeVelocity = THREE.MathUtils.lerp(1.2, 4.4, speedFactor);
  target.liftVelocity = THREE.MathUtils.lerp(0.75, 2.2, driftFactor);
  target.lifetime = THREE.MathUtils.lerp(0.34, 0.58, driftFactor);
  target.speedFactor = speedFactor;
  target.driftFactor = driftFactor;
  return target;
};

export type BoostOnsetProfile = {
  compression: number;
  durationSeconds: number;
};

const createEmptyBoostOnsetProfile = (): BoostOnsetProfile => ({
  compression: 0,
  durationSeconds: 0,
});

export const calculateBoostOnsetProfile = (
  boost: number,
  previousBoost: number,
  deltaSeconds: number,
  target: BoostOnsetProfile = createEmptyBoostOnsetProfile(),
): BoostOnsetProfile => {
  const safeBoost = Number.isFinite(boost)
    ? THREE.MathUtils.clamp(boost, 0, 1)
    : 0;
  const safePreviousBoost = Number.isFinite(previousBoost)
    ? THREE.MathUtils.clamp(previousBoost, 0, 1)
    : 0;
  const safeDelta = Number.isFinite(deltaSeconds)
    ? THREE.MathUtils.clamp(deltaSeconds, 0, 0.05)
    : 0;
  const risingEdge = safeDelta > 0 ? Math.max(0, safeBoost - safePreviousBoost) : 0;
  const compression = THREE.MathUtils.clamp(risingEdge * 1.1, 0, 0.9);

  target.compression = compression;
  target.durationSeconds = compression > 0 ? 0.32 : 0;
  return target;
};

export const calculateWetWakeProfile = (
  speed: number,
  slip = 0,
  drift = 0,
  steering = 0,
) => {
  const safeSpeed = Number.isFinite(speed) ? Math.max(0, speed) : 0;
  const safeSlip = Number.isFinite(slip)
    ? THREE.MathUtils.clamp(slip, -1, 1)
    : 0;
  const safeDrift = Number.isFinite(drift)
    ? THREE.MathUtils.clamp(drift, 0, 1)
    : 0;
  const safeSteering = Number.isFinite(steering)
    ? THREE.MathUtils.clamp(steering, -1, 1)
    : 0;
  const speedFactor = THREE.MathUtils.smoothstep(safeSpeed, 24, 84);
  const highSpeedFactor = THREE.MathUtils.smoothstep(safeSpeed, 48, 84);
  const presence = THREE.MathUtils.smoothstep(safeSpeed, 9, 25);
  const driftFactor = THREE.MathUtils.clamp(
    Math.abs(safeSlip) * 0.9 + safeDrift * 0.75 + Math.abs(safeSteering) * 0.14,
    0,
    1,
  );
  const signedBias = THREE.MathUtils.clamp(
    safeSlip * 0.78 + safeSteering * 0.32,
    -1,
    1,
  );
  const opacity =
    presence *
    (0.008 + speedFactor * 0.03 + highSpeedFactor * 0.018) *
    THREE.MathUtils.lerp(1, 1.34, driftFactor);

  return {
    speedFactor,
    highSpeedFactor,
    driftFactor,
    signedBias,
    length:
      (0.46 + speedFactor * 0.78 + highSpeedFactor * 0.34) *
      THREE.MathUtils.lerp(1, 1.08, driftFactor),
    width:
      (0.17 + speedFactor * 0.2 + highSpeedFactor * 0.16) *
      THREE.MathUtils.lerp(1, 1.58, driftFactor),
    spread:
      (0.72 + speedFactor * 0.1 + highSpeedFactor * 0.08) *
      THREE.MathUtils.lerp(1, 1.22, driftFactor),
    opacity,
    lifetime:
      (0.22 + speedFactor * 0.12 + highSpeedFactor * 0.08) *
      THREE.MathUtils.lerp(1, 1.18, driftFactor),
    emissionRate:
      presence *
      (18 + speedFactor * 24 + highSpeedFactor * 12) *
      THREE.MathUtils.lerp(1, 1.32, driftFactor),
    luminance: 0.4 + speedFactor * 0.08 + highSpeedFactor * 0.04,
    visible: presence > 0.001 && opacity > 0.002,
  };
};

export type WetWakeRibbonProfile = {
  lateralOffset: number;
  width: number;
  length: number;
  opacity: number;
  luminance: number;
  yaw: number;
  longitudinalJitter: number;
};

const createEmptyWetWakeRibbonProfile = (): WetWakeRibbonProfile => ({
  lateralOffset: 0,
  width: 0,
  length: 0,
  opacity: 0,
  luminance: 0,
  yaw: 0,
  longitudinalJitter: 0,
});

export const calculateWetWakeRibbonProfile = (
  profile: ReturnType<typeof calculateWetWakeProfile>,
  side: number,
  variation: number,
  target: WetWakeRibbonProfile = createEmptyWetWakeRibbonProfile(),
): WetWakeRibbonProfile => {
  const safeSide = side < 0 ? -1 : 1;
  const safeVariation = Number.isFinite(variation)
    ? THREE.MathUtils.clamp(variation, -1, 1)
    : 0;
  const signedSideBias = safeSide * profile.signedBias;
  const strength = THREE.MathUtils.clamp(1 + signedSideBias * 0.3, 0.7, 1.3);

  target.lateralOffset =
    safeSide * profile.spread * (1 + safeVariation * 0.22) +
    profile.signedBias * 0.12;
  target.width =
    profile.width *
    (1 - safeVariation * 0.18) *
    (1 + signedSideBias * 0.12);
  target.length =
    profile.length *
    (1 + safeVariation * 0.18) *
    (1 - signedSideBias * 0.1);
  target.opacity = profile.opacity * strength;
  target.luminance = profile.luminance * strength;
  target.yaw = -profile.signedBias * 0.13 + safeSide * safeVariation * 0.042;
  target.longitudinalJitter =
    profile.length * (safeSide < 0 ? -0.2 : 0.2) +
    profile.length * safeVariation * 0.08;
  return target;
};

export type SparkBurstProfile = {
  impulse: number;
  side: -1 | 0 | 1;
  count: number;
};

export const calculateSparkBurstProfile = (
  impact: number,
  previousImpact: number,
  impactSide: number,
  previousImpactSide: number,
  speed: number,
  quality: SuntraceQuality,
): SparkBurstProfile => {
  const normalizedImpact = Number.isFinite(impact)
    ? THREE.MathUtils.clamp(impact, 0, 1)
    : 0;
  const normalizedPreviousImpact = Number.isFinite(previousImpact)
    ? THREE.MathUtils.clamp(previousImpact, 0, 1)
    : 0;
  const side = impactSide === -1 || impactSide === 1 ? impactSide : 0;
  const previousSide = previousImpactSide === -1 || previousImpactSide === 1
    ? previousImpactSide
    : 0;
  const risingImpulse = Math.max(
    0,
    normalizedImpact - normalizedPreviousImpact,
  );
  const sideChangeImpulse =
    side !== 0 && side !== previousSide && normalizedImpact >= 0.2
      ? normalizedImpact * 0.72
      : 0;
  const impulse = side === 0
    ? 0
    : Math.max(risingImpulse, sideChangeImpulse);
  const speedFactor = THREE.MathUtils.smoothstep(
    Number.isFinite(speed) ? Math.max(0, speed) : 0,
    10,
    45,
  );
  const qualityCapacity = quality === "high" ? 34 : quality === "medium" ? 26 : 18;
  const count = impulse >= 0.08 && speedFactor > 0
    ? Math.max(1, Math.floor(impulse * speedFactor * qualityCapacity))
    : 0;

  return { impulse, side, count };
};

const createSkidMarkTexture = (): THREE.DataTexture => {
  const width = 32;
  const height = 64;
  const data = new Uint8Array(width * height * 4);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const normalizedX = Math.abs((x + 0.5) / width * 2 - 1);
      const normalizedY = (y + 0.5) / height;
      const endFade = Math.min(1, normalizedY * 9, (1 - normalizedY) * 9);
      const rubberCore = Math.pow(Math.max(0, 1 - normalizedX / 0.72), 0.62) * 0.66;
      const reflectiveEdge = Math.exp(-Math.pow((normalizedX - 0.79) / 0.075, 2)) * 0.22;
      const tonalWeight = rubberCore + reflectiveEdge;
      const reflectiveMix = reflectiveEdge / Math.max(0.001, tonalWeight);
      const rubberVariation = 0.84 + Math.sin(normalizedY * 47 + normalizedX * 8) * 0.1;
      const alpha = Math.round(Math.min(1, tonalWeight) * endFade * rubberVariation * 255);
      const offset = (y * width + x) * 4;
      data[offset] = Math.round(THREE.MathUtils.lerp(7, 132, reflectiveMix));
      data[offset + 1] = Math.round(THREE.MathUtils.lerp(9, 151, reflectiveMix));
      data[offset + 2] = Math.round(THREE.MathUtils.lerp(10, 154, reflectiveMix));
      data[offset + 3] = alpha;
    }
  }
  const texture = new THREE.DataTexture(data, width, height, THREE.RGBAFormat);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.minFilter = THREE.LinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
};

const createSkidMarks = (count: number): {
  mesh: THREE.InstancedMesh;
  geometry: THREE.PlaneGeometry;
  material: THREE.MeshBasicMaterial;
  emit: (
    frame: TrackFrame,
    side: number,
    intensity: number,
    longitudinalOffset: number,
    yaw: number,
  ) => void;
  setCapacity: (capacity: number) => void;
  dispose: () => void;
} => {
  const geometry = new THREE.PlaneGeometry(0.24, 0.9);
  geometry.rotateX(-Math.PI / 2);
  const texture = createSkidMarkTexture();
  const material = new THREE.MeshBasicMaterial({
    color: 0x161c20,
    map: texture,
    transparent: true,
    opacity: 0.46,
    alphaTest: 0.015,
    depthWrite: false,
    polygonOffset: true,
    polygonOffsetFactor: -1,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.renderOrder = 1;
  const dummy = new THREE.Object3D();
  const position = new THREE.Vector3();
  for (let index = 0; index < count; index += 1) {
    dummy.position.set(0, -10_000, 0);
    dummy.scale.setScalar(0.001);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
  }
  mesh.instanceMatrix.needsUpdate = true;
  let cursor = 0;
  let capacity = count;
  const localYaw = new THREE.Quaternion();
  const localUp = new THREE.Vector3(0, 1, 0);

  return {
    mesh,
    geometry,
    material,
    emit(frame, side, intensity, longitudinalOffset, yaw) {
      position
        .copy(frame.position)
        .addScaledVector(frame.right, side * 0.73)
        .addScaledVector(frame.tangent, -1.18 - longitudinalOffset)
        .addScaledVector(frame.normal, 0.028);
      dummy.position.copy(position);
      localYaw.setFromAxisAngle(localUp, yaw);
      dummy.quaternion.copy(frame.quaternion).multiply(localYaw);
      dummy.scale.set(0.76 + intensity * 0.38, 1, 0.82 + intensity * 0.5);
      dummy.updateMatrix();
      mesh.setMatrixAt(cursor, dummy.matrix);
      mesh.instanceMatrix.needsUpdate = true;
      cursor = (cursor + 1) % capacity;
    },
    setCapacity(nextCapacity) {
      capacity = THREE.MathUtils.clamp(Math.floor(nextCapacity), 2, count);
      mesh.count = capacity;
      cursor %= capacity;
    },
    dispose() {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
      texture.dispose();
    },
  };
};

const createRoadFlow = (count: number) => {
  const track = createSuntraceTrack();
  const geometry = new THREE.PlaneGeometry(1, 1);
  geometry.rotateX(-Math.PI / 2);
  const material = new THREE.MeshBasicMaterial({
    color: 0xb7e2e6,
    transparent: true,
    opacity: 0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    vertexColors: true,
    side: THREE.DoubleSide,
  });
  const mesh = new THREE.InstancedMesh(geometry, material, count);
  mesh.name = "Road flow reflectors";
  mesh.visible = false;
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  const progressAnchors = new Float64Array(count);
  const dummy = new THREE.Object3D();
  const color = new THREE.Color();
  const roadFrame = track.sample(0);
  const laneCount = 2;
  const instancesPerLane = count / laneCount;
  const laneOffset = track.width * 0.46;
  const spacing = 2.8;
  let initialized = false;
  let previousProgress = 0;

  const initialize = (trackProgress: number): void => {
    for (let index = 0; index < count; index += 1) {
      const laneIndex = index % laneCount;
      const laneSlot = Math.floor(index / laneCount);
      progressAnchors[index] = wrapProgress(
        trackProgress + (9.5 + laneSlot * spacing + laneIndex * spacing * 0.5) / track.length,
      );
    }
    initialized = true;
    previousProgress = trackProgress;
  };

  for (let index = 0; index < count; index += 1) {
    dummy.position.set(0, -10_000, 0);
    dummy.scale.setScalar(0.001);
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
    mesh.setColorAt(index, color.setRGB(0, 0, 0));
  }
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor !== null) {
    mesh.instanceColor.setUsage(THREE.DynamicDrawUsage);
    mesh.instanceColor.needsUpdate = true;
  }

  const disable = (): void => {
    mesh.visible = false;
    material.opacity = 0;
  };

  return {
    mesh,
    update(
      trackProgress: number,
      speed: number,
      boostCompression: number,
      playerFrame: TrackFrame,
      enabled: boolean,
    ) {
      const safeProgress = Number.isFinite(trackProgress) ? wrapProgress(trackProgress) : 0;
      const progressDelta = Math.abs(
        ((safeProgress - previousProgress + 1.5) % 1) - 0.5,
      );
      if (!initialized || progressDelta * track.length > spacing * instancesPerLane * 0.45) {
        initialize(safeProgress);
      }
      previousProgress = safeProgress;
      if (!enabled) {
        disable();
        return;
      }
      const speedFactor = THREE.MathUtils.smoothstep(
        Number.isFinite(speed) ? Math.max(0, speed) : 0,
        38,
        82,
      );
      mesh.visible = speedFactor > 0.001;
      material.opacity = speedFactor * 0.18 + boostCompression * 0.06;
      if (!mesh.visible) {
        return;
      }
      for (let laneIndex = 0; laneIndex < laneCount; laneIndex += 1) {
        let furthestDistance = 0;
        for (let index = laneIndex; index < count; index += laneCount) {
          const anchor = progressAnchors[index] ?? safeProgress;
          const distance = (((anchor - safeProgress + 1.5) % 1) - 0.5) * track.length;
          furthestDistance = Math.max(furthestDistance, distance);
        }
        for (let index = laneIndex; index < count; index += laneCount) {
          const anchor = progressAnchors[index] ?? safeProgress;
          const distance = (((anchor - safeProgress + 1.5) % 1) - 0.5) * track.length;
          if (distance < 8) {
            furthestDistance += spacing;
            progressAnchors[index] = wrapProgress(safeProgress + furthestDistance / track.length);
          }
        }
      }
      for (let index = 0; index < count; index += 1) {
        const side = index % laneCount === 0 ? -1 : 1;
        track.sample(progressAnchors[index] ?? safeProgress, side * laneOffset, roadFrame);
        const cameraDistance = roadFrame.position.distanceTo(playerFrame.position);
        const distanceFade =
          THREE.MathUtils.smoothstep(cameraDistance, 3, 14) *
          (1 - THREE.MathUtils.smoothstep(cameraDistance, 104, 138));
        const variation = ((index * 29) % 13) / 12;
        dummy.position.copy(roadFrame.position).addScaledVector(roadFrame.normal, 0.012);
        dummy.quaternion.copy(roadFrame.quaternion);
        dummy.scale.set(
          0.055 + variation * 0.025,
          1,
          1.15 + speedFactor * 1.5 + boostCompression * 1.35,
        );
        dummy.updateMatrix();
        mesh.setMatrixAt(index, dummy.matrix);
        mesh.setColorAt(
          index,
          color.setRGB(
            distanceFade * 0.72,
            distanceFade * 0.9,
            distanceFade,
          ),
        );
      }
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor !== null) {
        mesh.instanceColor.needsUpdate = true;
      }
    },
    disable,
    getProgress(index: number) {
      const safeIndex = THREE.MathUtils.clamp(Math.floor(index), 0, count - 1);
      return progressAnchors[safeIndex] ?? 0;
    },
    readMatrix(index: number, target: THREE.Matrix4) {
      const safeIndex = THREE.MathUtils.clamp(Math.floor(index), 0, count - 1);
      mesh.getMatrixAt(safeIndex, target);
      return target;
    },
    get laneOffset() {
      return laneOffset;
    },
    get matrixBuffer() {
      return mesh.instanceMatrix.array.buffer;
    },
    get colorBuffer() {
      return mesh.instanceColor?.array.buffer ?? null;
    },
    dispose() {
      mesh.removeFromParent();
      geometry.dispose();
      material.dispose();
    },
  };
};

export type SuntraceEffectsDiagnostics = {
  readonly sprayCapacity: number;
  readonly sprayActive: boolean;
  readonly roadFlowCount: number;
  readonly roadFlowVisible: boolean;
  readonly roadFlowOpacity: number;
  readonly roadFlowLaneOffset: number;
  readonly roadFlowMatrixBuffer: ArrayBufferLike;
  readonly roadFlowColorBuffer: ArrayBufferLike | null;
  readonly speedStreaksActive: boolean;
  readSprayAge: (index: number) => number;
  readSprayPosition: (index: number, target: THREE.Vector3) => THREE.Vector3;
  readRoadFlowProgress: (index: number) => number;
  readRoadFlowMatrix: (index: number, target: THREE.Matrix4) => THREE.Matrix4;
};

export type SuntraceEffects = {
  group: THREE.Group;
  update: (
    snapshot: RacePresentationSnapshot,
    frame: TrackFrame,
    deltaSeconds: number,
    reducedMotion: boolean,
  ) => void;
  setQuality: (quality: SuntraceQuality) => void;
  getDiagnostics: () => SuntraceEffectsDiagnostics;
  dispose: () => void;
};

export const createSuntraceEffects = (): SuntraceEffects => {
  const group = new THREE.Group();
  group.name = "Wet circuit effects";
  const spray = new ParticlePool(520, 0xbcc9ca, 0.42, false, 3.2);
  spray.material.opacity = 0.2;
  const wetWakes = [
    createWetWake(120, 0.17),
    createWetWake(120, 0.83),
  ] as const;
  const sparks = new ParticlePool(180, 0xff9f43, 0.25, true);
  const streaks = createSpeedStreaks(28);
  const roadFlow = createRoadFlow(96);
  const skidMarks = createSkidMarks(720);
  group.add(
    skidMarks.mesh,
    ...wetWakes.map((wetWake) => wetWake.mesh),
    spray.points,
    sparks.points,
    streaks.mesh,
    roadFlow.mesh,
  );
  const random = createRandom(0xf0175eed);
  const emissionPosition = new THREE.Vector3();
  const emissionVelocity = new THREE.Vector3();
  const streakVertices = new Float32Array(18);
  const sprayColor = new THREE.Color(0xbcc9ca);
  const wakeColor = new THREE.Color(0x9eb7bf);
  const sparkColor = new THREE.Color(0xffa13a);
  const wakeRibbonProfiles = [
    createEmptyWetWakeRibbonProfile(),
    createEmptyWetWakeRibbonProfile(),
  ] as const;
  const driftSprayProfile = createEmptyDriftSprayProfile();
  const boostOnsetProfile = createEmptyBoostOnsetProfile();
  let sprayBudget = 0;
  let wakeBudget = 0;
  let wakeSide = -1;
  let skidBudget = 0;
  let previousImpact = 0;
  let previousImpactSide = 0;
  let previousBoost = 0;
  let boostOnsetRemaining = 0;
  let boostOnsetDuration = 0.32;
  let boostOnsetPeak = 0;
  let quality: SuntraceQuality = "high";
  const diagnostics: SuntraceEffectsDiagnostics = {
    get sprayCapacity() {
      return spray.count;
    },
    get sprayActive() {
      return spray.points.visible && spray.ages.some((age) => age < 1);
    },
    get roadFlowCount() {
      return roadFlow.mesh.count;
    },
    get roadFlowVisible() {
      return roadFlow.mesh.visible;
    },
    get roadFlowOpacity() {
      return roadFlow.mesh.material.opacity;
    },
    get roadFlowLaneOffset() {
      return roadFlow.laneOffset;
    },
    get roadFlowMatrixBuffer() {
      return roadFlow.matrixBuffer;
    },
    get roadFlowColorBuffer() {
      return roadFlow.colorBuffer;
    },
    get speedStreaksActive() {
      return streaks.mesh.visible && streaks.material.opacity > 0;
    },
    readSprayAge(index) {
      const safeIndex = THREE.MathUtils.clamp(Math.floor(index), 0, spray.count - 1);
      return spray.ages[safeIndex] ?? 1;
    },
    readSprayPosition(index, target) {
      const safeIndex = THREE.MathUtils.clamp(Math.floor(index), 0, spray.count - 1);
      return target.fromArray(spray.positions, safeIndex * 3);
    },
    readRoadFlowProgress(index) {
      return roadFlow.getProgress(index);
    },
    readRoadFlowMatrix(index, target) {
      return roadFlow.readMatrix(index, target);
    },
  };

  const emitSpray = (
    snapshot: RacePresentationSnapshot,
    frame: TrackFrame,
    deltaSeconds: number,
  ): void => {
    const driftIntensity = THREE.MathUtils.clamp(
      Math.abs(snapshot.slip) * 1.1 + snapshot.drift * 0.8,
      0,
      1,
    );
    const profile = calculateDriftSprayProfile(
      snapshot.speed,
      driftIntensity,
      quality,
      false,
      driftSprayProfile,
    );
    sprayBudget = Math.min(
      12,
      sprayBudget + deltaSeconds * profile.emissionsPerSecond,
    );
    const emissions = Math.min(12, Math.floor(sprayBudget));
    sprayBudget -= emissions;
    for (let index = 0; index < emissions; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      emissionPosition
        .copy(frame.position)
        .addScaledVector(frame.right, side * (0.79 + (random() - 0.5) * profile.contactSpread))
        .addScaledVector(frame.tangent, -1.37 - random() * 0.16)
        .addScaledVector(frame.normal, 0.12 + random() * 0.06);
      emissionVelocity
        .copy(frame.tangent)
        .multiplyScalar(snapshot.speed * profile.velocityInheritance - profile.wakeVelocity)
        .addScaledVector(
          frame.right,
          side * (0.45 + random() * profile.contactSpread * 4) +
            (random() - 0.5) * profile.contactSpread * 2,
        )
        .addScaledVector(frame.normal, profile.liftVelocity * (0.72 + random() * 0.56));
      sprayColor.setHSL(0.51 + random() * 0.025, 0.08, 0.62 + random() * 0.1);
      spray.emit(
        emissionPosition,
        emissionVelocity,
        profile.lifetime * (0.84 + random() * 0.32),
        sprayColor,
      );
    }
    const wakeProfile = calculateWetWakeProfile(
      snapshot.speed,
      snapshot.slip,
      snapshot.drift,
      snapshot.steering,
    );
    const wakeQualityOpacity =
      quality === "high" ? 1 : quality === "medium" ? 0.82 : 0.68;
    for (let sideIndex = 0; sideIndex < wetWakes.length; sideIndex += 1) {
      const wetWake = wetWakes[sideIndex];
      const ribbonProfile = wakeRibbonProfiles[sideIndex];
      if (wetWake === undefined || ribbonProfile === undefined) {
        continue;
      }
      calculateWetWakeRibbonProfile(
        wakeProfile,
        sideIndex === 0 ? -1 : 1,
        0,
        ribbonProfile,
      );
      wetWake.material.opacity = ribbonProfile.opacity * wakeQualityOpacity;
    }
    if (!wakeProfile.visible) {
      return;
    }
    wakeBudget = Math.min(
      12,
      wakeBudget +
        deltaSeconds *
          wakeProfile.emissionRate *
          (quality === "high" ? 1 : quality === "medium" ? 0.76 : 0.55),
    );
    const wakeEmissions = Math.min(12, Math.floor(wakeBudget));
    wakeBudget -= wakeEmissions;
    for (let index = 0; index < wakeEmissions; index += 1) {
      const side = wakeSide;
      wakeSide *= -1;
      const variation = random() * 2 - 1;
      const wakePoolIndex = side < 0 ? 0 : 1;
      const wakeRibbonProfile = wakeRibbonProfiles[wakePoolIndex];
      calculateWetWakeRibbonProfile(
        wakeProfile,
        side,
        variation,
        wakeRibbonProfile,
      );
      emissionPosition
        .copy(frame.position)
        .addScaledVector(frame.right, wakeRibbonProfile.lateralOffset)
        .addScaledVector(
          frame.tangent,
          -1.32 -
            wakeRibbonProfile.length * 0.5 +
            wakeRibbonProfile.longitudinalJitter +
            (random() - 0.5) * wakeRibbonProfile.length * 0.18,
        )
        .addScaledVector(frame.normal, 0.032 + random() * 0.016);
      wakeColor.setHSL(
        0.51 + random() * 0.022,
        0.08 + random() * 0.045,
        THREE.MathUtils.clamp(
          wakeRibbonProfile.luminance + random() * 0.025,
          0.34,
          0.7,
        ),
      );
      wetWakes[wakePoolIndex].emit(
        emissionPosition,
        frame.quaternion,
        wakeProfile.lifetime * (0.88 + random() * 0.24),
        wakeRibbonProfile.length,
        wakeRibbonProfile.width,
        wakeColor,
        wakeRibbonProfile.yaw,
      );
    }
  };

  const emitSparks = (
    snapshot: RacePresentationSnapshot,
    frame: TrackFrame,
  ): void => {
    const profile = calculateSparkBurstProfile(
      snapshot.impact,
      previousImpact,
      snapshot.impactSide,
      previousImpactSide,
      snapshot.speed,
      quality,
    );
    for (let index = 0; index < profile.count; index += 1) {
      emissionPosition
        .copy(frame.position)
        .addScaledVector(frame.right, profile.side * 1.06)
        .addScaledVector(frame.tangent, -0.95 + random() * 1.4)
        .addScaledVector(frame.normal, 0.22 + random() * 0.08);
      emissionVelocity
        .copy(frame.right)
        .multiplyScalar(-profile.side * (1.8 + random() * 5.4))
        .addScaledVector(frame.tangent, -3.5 - random() * 5.5)
        .addScaledVector(frame.normal, 1.4 + random() * 5.2);
      sparkColor.setHSL(0.055 + random() * 0.055, 1, 0.58 + random() * 0.28);
      sparks.emit(
        emissionPosition,
        emissionVelocity,
        0.16 + random() * 0.24,
        sparkColor,
      );
    }
    previousImpact = snapshot.impact;
    previousImpactSide = snapshot.impact < 0.02 ? 0 : profile.side;
  };

  const updateSpeedStreaks = (
    snapshot: RacePresentationSnapshot,
    frame: TrackFrame,
    boostCompression: number,
  ): void => {
    const profile = calculateSpeedStreakProfile(snapshot.speed, snapshot.boost);
    const qualityOpacity = quality === "low" ? 0.7 : quality === "medium" ? 0.86 : 1;
    streaks.mesh.visible = profile.visible;
    streaks.material.opacity = profile.opacity * qualityOpacity;
    if (!streaks.mesh.visible) {
      return;
    }
    const count = streaks.positions.length / 18;
    const qualityCount = quality === "high" ? count : quality === "medium" ? 18 : 10;
    const visibleRatio = THREE.MathUtils.clamp(
      0.58 + profile.speedIntensity * 0.18 + profile.velocitySeparation * 0.24,
      0,
      1,
    );
    const visibleCount = Math.max(2, Math.round(qualityCount * visibleRatio));
    streaks.geometry.setDrawRange(0, visibleCount * 6);
    for (let index = 0; index < count; index += 1) {
      const side = index % 2 === 0 ? -1 : 1;
      const variation = ((index * 37) % 11) / 10;
      const lateral = side * (5.2 + (index % 4) * 0.64 + variation * 0.18);
      const vertical = 0.13 + (index % 4) * 0.095 + variation * 0.06;
      const travel =
        (snapshot.time * snapshot.speed * (0.5 + variation * 0.14) + index * 3.71) % 15.8;
      const longitudinal = 21 + 15.8 - travel;
      emissionPosition
        .copy(frame.position)
        .addScaledVector(frame.right, lateral)
        .addScaledVector(frame.normal, vertical)
        .addScaledVector(frame.tangent, longitudinal);
      const headX = emissionPosition.x;
      const headY = emissionPosition.y;
      const headZ = emissionPosition.z;
      const streakLength =
        (2.2 +
          profile.speedIntensity * 4.2 +
          profile.velocitySeparation * 3.5 +
          snapshot.boost * 1.4 +
          boostCompression * 1.35) *
        (0.72 + variation * 0.46);
      const streakWidth = THREE.MathUtils.lerp(
        profile.minimumWidth,
        profile.maximumWidth,
        variation,
      );
      emissionPosition
        .addScaledVector(frame.tangent, -streakLength)
        .addScaledVector(frame.right, side * (0.025 + variation * 0.045))
        .addScaledVector(frame.normal, 0.018 + variation * 0.038);
      const tailX = emissionPosition.x;
      const tailY = emissionPosition.y;
      const tailZ = emissionPosition.z;
      const headWidth = streakWidth * 0.5;
      const tailWidth = streakWidth * 0.08;
      const rightX = frame.right.x;
      const rightY = frame.right.y;
      const rightZ = frame.right.z;
      const offset = index * 18;
      streakVertices.set([
        headX - rightX * headWidth,
        headY - rightY * headWidth,
        headZ - rightZ * headWidth,
        tailX - rightX * tailWidth,
        tailY - rightY * tailWidth,
        tailZ - rightZ * tailWidth,
        headX + rightX * headWidth,
        headY + rightY * headWidth,
        headZ + rightZ * headWidth,
        headX + rightX * headWidth,
        headY + rightY * headWidth,
        headZ + rightZ * headWidth,
        tailX - rightX * tailWidth,
        tailY - rightY * tailWidth,
        tailZ - rightZ * tailWidth,
        tailX + rightX * tailWidth,
        tailY + rightY * tailWidth,
        tailZ + rightZ * tailWidth,
      ]);
      streaks.positions.set(streakVertices, offset);
    }
    streaks.geometry.getAttribute("position").needsUpdate = true;
  };

  const updateSkidMarks = (
    snapshot: RacePresentationSnapshot,
    frame: TrackFrame,
    deltaSeconds: number,
  ): void => {
    const driftIntensity = THREE.MathUtils.clamp(
      Math.abs(snapshot.slip) * 1.35 + snapshot.drift * 0.75,
      0,
      1,
    );
    if (snapshot.speed < 18 || driftIntensity < 0.18) {
      return;
    }
    const segmentSpacing = THREE.MathUtils.lerp(1.05, 0.7, driftIntensity);
    skidBudget = Math.min(5, skidBudget + deltaSeconds * snapshot.speed / segmentSpacing);
    const emissions = Math.min(5, Math.floor(skidBudget));
    skidBudget -= emissions;
    for (let index = 0; index < emissions; index += 1) {
      const longitudinalOffset = index * segmentSpacing;
      const yaw = -snapshot.slip * 0.18 + (random() - 0.5) * 0.018;
      skidMarks.emit(frame, -1, driftIntensity, longitudinalOffset, yaw);
      skidMarks.emit(frame, 1, driftIntensity, longitudinalOffset, yaw);
    }
  };

  return {
    group,
    update(snapshot, frame, deltaSeconds, reducedMotion) {
      spray.points.visible = !reducedMotion;
      for (const wetWake of wetWakes) {
        wetWake.mesh.visible = !reducedMotion;
      }
      sparks.points.visible = !reducedMotion;
      if (reducedMotion) {
        streaks.mesh.visible = false;
        roadFlow.disable();
        previousBoost = snapshot.boost;
        boostOnsetRemaining = 0;
        boostOnsetPeak = 0;
        return;
      }
      if (deltaSeconds <= 0) {
        return;
      }
      const boostOnset = calculateBoostOnsetProfile(
        snapshot.boost,
        previousBoost,
        deltaSeconds,
        boostOnsetProfile,
      );
      previousBoost = snapshot.boost;
      if (boostOnset.compression > 0) {
        boostOnsetDuration = boostOnset.durationSeconds;
        boostOnsetRemaining = boostOnset.durationSeconds;
        boostOnsetPeak = boostOnset.compression;
      } else {
        boostOnsetRemaining = Math.max(0, boostOnsetRemaining - deltaSeconds);
      }
      const boostCompression =
        boostOnsetPeak *
        THREE.MathUtils.smoothstep(
          boostOnsetRemaining / Math.max(0.001, boostOnsetDuration),
          0,
          1,
        );
      emitSpray(snapshot, frame, deltaSeconds);
      emitSparks(snapshot, frame);
      spray.update(deltaSeconds, 1.8, -0.5);
      for (const wetWake of wetWakes) {
        wetWake.update(deltaSeconds);
      }
      sparks.update(deltaSeconds, 0.65, 11.5);
      updateSkidMarks(snapshot, frame, deltaSeconds);
      updateSpeedStreaks(snapshot, frame, boostCompression);
      roadFlow.update(
        snapshot.trackProgress,
        snapshot.speed,
        boostCompression,
        frame,
        quality === "high",
      );
    },
    setQuality(nextQuality) {
      quality = nextQuality;
      spray.material.size = nextQuality === "high" ? 0.48 : nextQuality === "medium" ? 0.4 : 0.34;
      const wakeCapacity = nextQuality === "high" ? 120 : nextQuality === "medium" ? 90 : 60;
      for (const wetWake of wetWakes) {
        wetWake.setCapacity(wakeCapacity);
      }
      sparks.material.size = nextQuality === "high" ? 0.18 : nextQuality === "medium" ? 0.16 : 0.14;
      streaks.geometry.setDrawRange(
        0,
        nextQuality === "high" ? 168 : nextQuality === "medium" ? 108 : 60,
      );
      skidMarks.setCapacity(nextQuality === "high" ? 720 : nextQuality === "medium" ? 480 : 300);
      if (nextQuality !== "high") {
        roadFlow.disable();
      }
    },
    getDiagnostics() {
      return diagnostics;
    },
    dispose() {
      spray.dispose();
      for (const wetWake of wetWakes) {
        wetWake.dispose();
      }
      sparks.dispose();
      skidMarks.dispose();
      streaks.mesh.removeFromParent();
      streaks.geometry.dispose();
      streaks.material.dispose();
      roadFlow.dispose();
      group.removeFromParent();
      group.clear();
    },
  };
};
